import { SignIn } from "@clerk/tanstack-react-start";
import { useAtomValue } from "@effect-atom/atom-react";
import { createFileRoute } from "@tanstack/react-router";
import type { ComponentProps } from "react";
import { clawInstallIdentityAtom } from "~/lib/atoms/brand-identity.ts";
import { type ClawInstallMode, clawInstallModeAtom } from "~/lib/atoms/clawinstall-theme.ts";
import { loginHead, validateLoginSearch } from "~/lib/login-search.ts";

/** Clerk's `appearance` shape, typed without adding a dependency. */
type SignInAppearance = NonNullable<ComponentProps<typeof SignIn>["appearance"]>;

/**
 * Clerk appearance for the customer sub-theme; hex values mirror
 * `globals.css:540-609`, except the three input-field values (`#fbfdfd`,
 * `#163653`, `#081e31`), which come from the design captures
 * (`docs/design/clawinstall-customer-onboarding/light/_group.css:1`,
 * `docs/design/clawinstall-reseller-admin/_group.css:1`) — Clerk's API takes
 * colours, not classes. The two modes track `clawInstallModeAtom`.
 *
 * @since 1.0.0
 */
export const signInAppearance = (mode: ClawInstallMode): SignInAppearance => ({
  variables: mode === "dark"
    ? {
      colorPrimary: "#f58687",
      colorPrimaryForeground: "#271a24",
      colorBackground: "#0b2439",
      colorForeground: "#edf7ff",
      colorMutedForeground: "#8eacbf",
      colorInput: "#081e31",
      colorInputForeground: "#edf7ff",
      colorBorder: "#1f4059",
      borderRadius: "0.75rem",
      fontFamily: "'DM Sans', 'DM Sans Fallback', sans-serif",
    }
    : {
      colorPrimary: "#db625d",
      colorPrimaryForeground: "#ffffff",
      colorBackground: "#fffdf8",
      colorForeground: "#102946",
      colorMutedForeground: "#60748a",
      colorInput: "#fbfdfd",
      colorInputForeground: "#163653",
      colorBorder: "#cbdce9",
      borderRadius: "0.75rem",
      fontFamily: "'DM Sans', 'DM Sans Fallback', sans-serif",
    },
  elements: {
    cardBox: "shadow-[0_16px_34px_rgba(31,75,108,0.08)]",
    card: "rounded-2xl border border-outline-variant",
    headerTitle: "font-headline-md tracking-tighter",
    formButtonPrimary: "font-bold",
    footerActionLink: "text-secondary",
  },
});

/**
 * ClawInstall customer login page
 *
 * Renders the Clerk sign-in component with identity-driven branding on the
 * customer sub-theme. Reachable at `clawinstall.ai/login` (the domain router
 * maps the public `/login` onto the internal `/clawinstall/login`). This is the
 * brand's customer door — it encodes *intent* ("sign in to my account"), so
 * after sign-in it lands the user on their customer dashboard regardless of
 * role. Admins who want the admin console use `/admin/login` instead.
 * Authorization for the admin area is enforced by the `/admin` route guard, not
 * by this redirect.
 *
 * The `/clawinstall` layout already provides the `.theme-clawinstall` ancestor
 * carrying `data-mode`, so the customer palette resolves inside this page; the
 * brand lockup and `<h1>` render `identity.displayName`, and the Clerk widget is
 * tinted via {@link signInAppearance}. `routing`, `path` and `fallbackRedirectUrl`
 * are untouched — Clerk's path-routed `<SignIn>` renders nothing if `path` does
 * not match the browser URL.
 *
 * @since 1.0.0
 */
export const Route = createFileRoute("/clawinstall/login/")({
  validateSearch: validateLoginSearch,
  head: ({ match }) => loginHead(match.context.identity?.displayName ?? "ClawInstall"),
  component: ClawInstallLogin,
});

export function ClawInstallLogin() {
  const identity = useAtomValue(clawInstallIdentityAtom);
  const mode = useAtomValue(clawInstallModeAtom);
  return (
    <div className="theme-clawinstall-customer flex min-h-screen items-center justify-center bg-surface px-6 font-sans text-on-surface">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <span
            aria-hidden="true"
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#09090b] text-xl font-bold leading-none text-white"
          >
            {identity.monogram}
          </span>
          <h1 className="font-headline-md text-headline-md tracking-tighter text-on-surface">
            {identity.displayName}
          </h1>
        </div>
        <SignIn
          routing="path"
          path="/login"
          fallbackRedirectUrl="/clawinstall/dashboard"
          appearance={signInAppearance(mode)}
        />
      </div>
    </div>
  );
}
